// ⚠️ PLAYGROUND-ONLY: This file depends on Firebase and should ONLY be imported by PlaygroundHost.
// Do NOT import from barrel exports (ai/index.ts).

import {
  AiApi,
  AiChatCompletionData,
  AiChatCompletionRequest,
  AiChatCompletionStreamChunk,
  AiChatCompletionStreamOptions,
  PromptCompletionRequest,
  toPromptWireRequest,
  AiDecideRequest,
  AiDecideResponse,
  AiDecideQuestions,
} from './AiApi'
import { CreditsExhaustedError, type CreditsBilledTo } from '../credits/CreditsApi'
import { RundotApiError } from '../errors'
import { getFirebaseClient } from '../firebase/firebaseClient'
import { getCloudRunUrl } from '../config/playground'
import { getPlaygroundGameSessionToken } from '../http/playgroundGameSession'
import { serializeCanvasInState } from './canvasUtils'

/**
 * JSON body POSTed to /v1/llm/completion(-stream), minus the `gameId` the
 * class appends: an open-mode chat request (sans the deprecated `apiKey`) or
 * a templated prompt-only request. The server's route-body mapping tells
 * them apart by shape.
 */
type WireCompletionBody =
  | Omit<AiChatCompletionRequest, 'apiKey'>
  | PromptCompletionRequest

export class HttpAiApi implements AiApi {
  private readonly gameId: string

  constructor(gameId: string) {
    this.gameId = gameId
  }


  async decide<const Q extends AiDecideQuestions>(
    request: AiDecideRequest<Q>,
  ): Promise<AiDecideResponse<Q>> {
    const client = await getFirebaseClient()
    const idToken = await client.getIdTokenOrThrow()
    const baseUrl = getCloudRunUrl()
    const url = `${baseUrl}/v1/ai/decide`

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      "x-consumer": "sdk",
    }
    const gst = await getPlaygroundGameSessionToken()
    if (gst) headers["x-h5-game-session"] = gst

    const state = serializeCanvasInState(request.state) as Record<string, unknown> | string

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ ...request, state, gameId: this.gameId }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      let errorMessage = `AI decide failed (${response.status})`
      let errorJson: Record<string, unknown> | null = null
      try {
        errorJson = JSON.parse(errorText) as Record<string, unknown>
        errorMessage =
          (errorJson.error as string) || (errorJson.message as string) || errorMessage
      } catch {
        if (errorText) errorMessage = errorText
      }
      if (response.status === 402) {
        // Credit exhaustion is the same typed failure the RPC path raises, so
        // a caller handles it once instead of parsing HTTP status codes.
        throw new CreditsExhaustedError(errorMessage, {
          billedTo: (errorJson?.billedTo as CreditsBilledTo) ?? 'user',
          // This adapter shows no paywall; only the host RPC path can.
          paywallShown: false,
          paywallOutcome: null,
        })
      }
      throw new Error(errorMessage)
    }

    return response.json() as Promise<AiDecideResponse<Q>>
  }

  async requestChatCompletionAsync(request: AiChatCompletionRequest): Promise<AiChatCompletionData> {
    const { apiKey: _apiKey, ...requestBody } = request
    return this.postCompletion(requestBody)
  }

  async requestPromptCompletionAsync(request: PromptCompletionRequest): Promise<AiChatCompletionData> {
    return this.postCompletion(toPromptWireRequest(request))
  }

  /** Shared non-streaming POST for both open-mode and templated completions. */
  private async postCompletion(requestBody: WireCompletionBody): Promise<AiChatCompletionData> {
    const client = await getFirebaseClient()
    const idToken = await client.getIdTokenOrThrow()
    const baseUrl = getCloudRunUrl()
    const url = `${baseUrl}/v1/llm/completion`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
      'x-consumer': 'sdk',
    }
    const gst = await getPlaygroundGameSessionToken()
    if (gst) headers['x-h5-game-session'] = gst

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ...requestBody, gameId: this.gameId }),
    })

    if (!response.ok) {
      const errorText = await response.text()
      let errorMessage = `AI completion failed (${response.status})`
      try {
        const errorJson = JSON.parse(errorText)
        errorMessage = errorJson.error || errorJson.message || errorMessage
      } catch {
        if (errorText) errorMessage = errorText
      }
      throw new Error(errorMessage)
    }

    return response.json()
  }

  async getAvailableCompletionModels(): Promise<Array<string>> {
    const client = await getFirebaseClient()
    const idToken = await client.getIdTokenOrThrow()
    const baseUrl = getCloudRunUrl()
    const url = `${baseUrl}/v1/llm/models?gameId=${encodeURIComponent(this.gameId)}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
      'x-consumer': 'sdk',
    }
    const gst = await getPlaygroundGameSessionToken()
    if (gst) headers['x-h5-game-session'] = gst

    const response = await fetch(url, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to get models (${response.status}): ${errorText}`)
    }

    return response.json()
  }

  requestChatCompletionStreamAsync(
    request: AiChatCompletionRequest,
    options?: AiChatCompletionStreamOptions,
  ): AsyncIterable<AiChatCompletionStreamChunk> {
    const { apiKey: _apiKey, ...requestBody } = request
    return this.openCompletionStream(requestBody, options)
  }

  requestPromptCompletionStreamAsync(
    request: PromptCompletionRequest,
    options?: AiChatCompletionStreamOptions,
  ): AsyncIterable<AiChatCompletionStreamChunk> {
    return this.openCompletionStream(toPromptWireRequest(request), options)
  }

  /**
   * Shared SSE machinery for both open-mode and templated streams: fetch,
   * buffer parsing, chunk yielding, and abort handling. The request body is
   * the only thing that differs between the two public methods.
   */
  private openCompletionStream(
    requestBody: WireCompletionBody,
    options?: AiChatCompletionStreamOptions,
  ): AsyncIterable<AiChatCompletionStreamChunk> {
    const gameId = this.gameId
    return {
      [Symbol.asyncIterator]: (): AsyncIterator<AiChatCompletionStreamChunk> => {
        let reader: ReadableStreamDefaultReader<Uint8Array> | null = null
        const abortController = new AbortController()
        if (options?.signal) {
          if (options.signal.aborted) {
            abortController.abort()
          } else {
            options.signal.addEventListener('abort', () => abortController.abort(), { once: true })
          }
        }

        let started = false
        let startPromise: Promise<void> | null = null
        const decoder = new TextDecoder()
        let buffer = ''
        // Parsed-but-not-yet-yielded chunks. Producers (parseBuffer) push; the
        // consumer's next() shifts one per iteration so a single network
        // chunk producing multiple SSE events still yields one chunk per call.
        const pending: Array<{ event: string; data: unknown }> = []
        let terminalError: Error | null = null
        let terminated = false
        // `yieldedTerminal` flips when a `done` or `error` event reaches the
        // consumer. Pairs with `userReturned` to tell legitimate end-of-stream
        // (consumer broke out → return()) apart from a truncated SSE body
        // (server closed the socket without sending `done`), which is a bug
        // we must surface, not silently coerce to a normal completion.
        let yieldedTerminal = false
        let userReturned = false

        const start = async (): Promise<void> => {
          const client = await getFirebaseClient()
          const idToken = await client.getIdTokenOrThrow()
          const baseUrl = getCloudRunUrl()
          const url = `${baseUrl}/v1/llm/completion-stream`
          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            'Authorization': `Bearer ${idToken}`,
            'x-consumer': 'sdk',
          }
          const gst = await getPlaygroundGameSessionToken()
          if (gst) headers['x-h5-game-session'] = gst
          const response = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify({ ...requestBody, gameId }),
            signal: abortController.signal,
          })
          if (!response.ok) {
            const errorText = await response.text().catch(() => '')
            let errorMessage = `AI streaming completion failed (${response.status})`
            let code = 'HTTP_ERROR'
            try {
              const errorJson = JSON.parse(errorText) as { error?: unknown; message?: unknown }
              if (typeof errorJson.error === 'string') code = errorJson.error
              if (typeof errorJson.message === 'string' && errorJson.message.length > 0) {
                errorMessage = errorJson.message
              } else if (typeof errorJson.error === 'string') {
                errorMessage = errorJson.error
              }
            } catch {
              if (errorText) errorMessage = errorText
            }
            throw new RundotApiError(code, errorMessage, response.status)
          }
          if (!response.body) {
            throw new RundotApiError(
              'NETWORK_ERROR',
              'AI streaming completion: response has no body',
            )
          }
          reader = response.body.getReader()
        }

        const parseBuffer = (): void => {
          // SSE events are separated by blank lines (`\n\n`). Anything before
          // the first separator we haven't consumed yet stays in `buffer`.
          while (true) {
            const sep = buffer.indexOf('\n\n')
            if (sep === -1) return
            const rawEvent = buffer.slice(0, sep)
            buffer = buffer.slice(sep + 2)
            let event = 'message'
            const dataLines: string[] = []
            for (const line of rawEvent.split('\n')) {
              if (line.startsWith('event:')) {
                event = line.slice('event:'.length).trim()
              } else if (line.startsWith('data:')) {
                dataLines.push(line.slice('data:'.length).trimStart())
              }
            }
            if (dataLines.length === 0) continue
            try {
              const data = JSON.parse(dataLines.join('\n'))
              pending.push({ event, data })
            } catch {
              // Skip malformed payload; producer-side bug.
            }
          }
        }

        const pumpUntilEvent = async (): Promise<void> => {
          while (pending.length === 0 && !terminated) {
            if (!reader) throw terminalError ?? new Error('AI streaming completion: reader missing')
            const { value, done } = await reader.read()
            if (done) {
              terminated = true
              return
            }
            buffer += decoder.decode(value, { stream: true })
            parseBuffer()
          }
        }

        return {
          next: async (): Promise<IteratorResult<AiChatCompletionStreamChunk>> => {
            if (!started) {
              started = true
              startPromise = start()
            }
            try {
              await startPromise
              await pumpUntilEvent()
            } catch (err) {
              terminalError = err instanceof Error ? err : new Error(String(err))
              terminated = true
              throw terminalError
            }

            if (pending.length === 0) {
              if (!yieldedTerminal && !userReturned) {
                throw new RundotApiError(
                  'NETWORK_ERROR',
                  'AI streaming completion ended unexpectedly without a terminal done event',
                )
              }
              return { value: undefined, done: true }
            }
            const { event, data } = pending.shift()!
            if (event === 'error') {
              yieldedTerminal = true
              const e = data as { error?: string; message?: string; code?: string; statusCode?: number }
              const code =
                typeof e.code === 'string' ? e.code
                : typeof e.error === 'string' ? e.error
                : 'STREAM_FAILED'
              throw new RundotApiError(
                code,
                e.message ?? e.error ?? 'AI streaming completion failed',
                typeof e.statusCode === 'number' ? e.statusCode : 0,
              )
            }
            if (event === 'done') {
              yieldedTerminal = true
              const payload = data as {
                finishReason: string
                usage: AiChatCompletionData['usage']
                model?: string
              }
              return {
                value: {
                  type: 'done',
                  finishReason: payload.finishReason,
                  usage: payload.usage,
                  // Resolved friendly model id; absent from older backends.
                  ...(payload.model !== undefined && { model: payload.model }),
                },
                done: false,
              }
            }
            // `chunk` event — body is the raw discriminated chunk.
            return {
              value: data as AiChatCompletionStreamChunk,
              done: false,
            }
          },
          return: async (): Promise<IteratorResult<AiChatCompletionStreamChunk>> => {
            userReturned = true
            terminated = true
            abortController.abort()
            try { await reader?.cancel() } catch { /* socket already gone */ }
            return { value: undefined, done: true }
          },
        }
      },
    }
  }
}
